/**
 * @file jobStore.ts
 *
 * @description This module provides a centralized state management system for tracking and managing
 * video evidence processing jobs. It handles job creation, status updates, progress tracking,
 * and automatic polling of job status from the backend Django API.
 *
 * The store is persisted to localStorage to maintain job state across browser sessions,
 * which is essential for long-running processing tasks.
 *
 * @module Store
 * @requires zustand
 * @requires jobsClient
 * @requires js-cookie
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { isAxiosError } from "axios";
import { ReportJobStatus } from "../shared/interfaces/JobStatus";
import { jobsClient } from "../api/clients";

const RESUMABLE_STATUSES = new Set<ReportJobStatus>([
  ReportJobStatus.PENDING,
  ReportJobStatus.STARTED,
  ReportJobStatus.GENERATING,
  ReportJobStatus.EXTRACTING,
]);

const PENDING_PROGRESS_STATUSES = new Set<ReportJobStatus>([
  ReportJobStatus.PENDING_REVIEW,
]);

// Represents a video processing job
export interface Job {
  id: string; // Unique identifier for the job
  status: ReportJobStatus; // Current status ('pending' | 'completed' | 'failed') of the job
  progress: number; // Progress percentage (0-100)
  reportName?: string; // Name of generated report when created
  details?: string; // Additional details information about the job
  error?: string; // Error message if job failed
  evidenceIDs: string[]; // Array of evidenceIDs being processed by this job
  createdAt: Date; // When the job was created
  displayStartedAt?: Date; // When the current user-visible activity cycle started
  selectedFields?: string[]; // Specific report fields selected for extraction
  videoInputLanguage?: string; // Language of the input video(s)
  progressMessage?: string; // Current status message indicating processing stage
  reportId?: string; // ID of the report created in the RMS
  sparseInfo?: Record<string, unknown>; // Additional metadata about the processed evidence
  videoTitles?: string[]; // Title of the video being processed
}

// Job management state and functions
interface JobState {
  jobs: Job[]; // List of all jobs (current and completed)
  /**
   * Adds a new job to the store
   * @param job - The job to add (without createdAt which will be automatically set)
   */
  addJob: (job: Omit<Job, "createdAt" | "displayStartedAt">) => void;
  /**
   * Updates the status of an existing job
   * @param id - ID of the job to update
   * @param status - New status value
   * @param reportId - Optional report ID (for completed jobs)
   * @param error - Optional error message (for failed jobs)
   */
  updateJobStatus: (
    id: string,
    status: ReportJobStatus,
    reportId?: string,
    error?: string
  ) => void;
  /**
   * Updates the progress of a job
   * @param id - ID of the job to update
   * @param progress - Progress percentage (0-100)
   * @param progressMessage - Optional message describing current progress stage
   */
  updateJobProgress: (
    id: string,
    progress: number,
    progressMessage?: string
  ) => void;
  /**
   * Updates a job with completion data
   * @param id - ID of the job to update
   * @param incidentID - ID of the created incident in the RMS
   * @param sparseInfo - Optional additional metadata
   */
  updateJobCompletionData: (
    id: string,
    reportId: string,
  ) => void;
  /**
   * Removes a job from the store
   * @param id - ID of the job to remove
   */
  removeJob: (id: string) => void;
  /**
   * Checks the current status of a job with the backend API
   * @param jobId - ID of the job to check
   * @returns Promise resolving to true if polling should stop, false to continue
   */
  checkJobStatus: (jobId: string) => Promise<boolean>;
  /**
   * Retries a failed job by creating a new job with the same parameters
   * @param job - The failed job to retry
   */
  retrySingleJob: (job: Job) => Promise<void>;
  /**
   * Gets the count of currently active (pending) jobs
   * @returns Number of active jobs
   */
  activeJobsCount: () => number;
  /**
   * Checks if the maximum number of concurrent jobs has been reached
   * @returns true if max jobs reached, false otherwise
   */
  isMaxJobsReached: () => boolean;
  /**
   * Starts polling for updates on a specific job
   * @param jobId - ID of the job to poll
   */
  startPollingJob: (jobId: string) => void;
  /**
   * Stops polling for a specific job
   * @param jobId - ID of the job to stop polling
   */
  stopPollingJob: (jobId: string) => void;
  // Maximum number of concurrent jobs allowed
  MAX_CONCURRENT_JOBS: number;
  // Record of active polling intervals by job ID
  pollingIntervals: Record<string, number>;
  // Clear persist store in event of logout/timeout
  clearStore: () => void;
}

/**
 * @name useJobStore
 *
 * @description Zustand store for managing video evidence processing jobs
 */
export const useJobStore = create<JobState>()(
  persist(
    (set, get) => ({
      jobs: [],
      MAX_CONCURRENT_JOBS: 3, // Maximum number of concurrent jobs
      pollingIntervals: {}, // Track polling intervals by job ID

      // Adds a new job to the store and starts polling for updates
      addJob: (job) => {
        const now = new Date();
        set((state) => ({
          jobs: [{ ...job, createdAt: now, displayStartedAt: now }, ...state.jobs],
        }));
        // Automatically start polling for the new job
        get().startPollingJob(job.id);
      },

      /**
       * Updates the status of an existing job
       * Also stops polling if job is completed or failed
       */
      updateJobStatus: (id, status, reportId, error) => {
        const currentJob = get().jobs.find((job) => job.id === id);
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === id
              ? {
                  ...job,
                  status,
                  reportName: reportId,
                  error,
                  displayStartedAt:
                    status === ReportJobStatus.PENDING &&
                    currentJob?.status === ReportJobStatus.AWAITING_INPUT
                      ? new Date()
                      : job.displayStartedAt,
                  progress: PENDING_PROGRESS_STATUSES.has(status)
                    ? 100
                    : job.progress,
                }
              : job
          ),
        }));

        // Pause polling while the job is waiting on human input.
        if (status === ReportJobStatus.AWAITING_INPUT) {
          get().stopPollingJob(id);
          return;
        }

        // Resume polling when a paused job moves back into a running state.
        if (
          status === ReportJobStatus.PENDING &&
          currentJob?.status === ReportJobStatus.AWAITING_INPUT
        ) {
          get().startPollingJob(id);
          return;
        }

        // Stop polling once the job reaches a terminal state.
        if (status === ReportJobStatus.PENDING_REVIEW || status === ReportJobStatus.FAILED) {
          get().stopPollingJob(id);
        }
      },

      // Updates the progress of a job
      updateJobProgress: (id, progress, progressMessage) => {
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === id
              ? {
                  ...job,
                  progress,
                  progressMessage: progressMessage || job.progressMessage,
                }
              : job
          ),
        }));
      },

      // Removes a job from the store and stops any active polling
      updateJobCompletionData: (id, reportId) => {
        set((state) => ({
          jobs: state.jobs.map((job) =>
            job.id === id
              ? {
                  ...job,
                  reportId,
                  status: ReportJobStatus.PENDING_REVIEW,
                  progress: 100,
                }
              : job
          ),
        }));
      },

      removeJob: (id) => {
        // Stop any polling for this job first
        get().stopPollingJob(id);

        set((state) => ({
          jobs: state.jobs.filter((job) => job.id !== id),
        }));
      },

      /**
       * Checks the current status of a job with the backend API
       * Updates job state based on API response
       * @returns Promise resolving to true if polling should stop, false to continue
       */
      checkJobStatus: async (jobId) => {
        const job = get().jobs.find((job) => job.id === jobId);
        if (
          !job ||
          (job.status !== ReportJobStatus.PENDING &&
            job.status !== ReportJobStatus.AWAITING_INPUT)
        ) {
          return true;
        }

        try {
          const response = await jobsClient.get("/jobStatus", {
            params: { jobId },
          });

          if (response.status !== 200) {
            throw new Error("Failed to fetch job status");
          }

          const { jobStatus, progress, message } = response.data;

          if (jobStatus === ReportJobStatus.AWAITING_INPUT) {
            get().updateJobStatus(jobId, ReportJobStatus.AWAITING_INPUT);
            get().updateJobProgress(jobId, progress ?? job.progress, message);
            return false;
          }

          // If status 202, job is still in progress
          if (jobStatus === ReportJobStatus.PENDING_REVIEW) {
            const { reportId } = response.data;
            get().updateJobCompletionData(jobId, reportId);
            get().updateJobProgress(jobId, progress ?? 100, message);
            return true; // Stop polling
          } else if (RESUMABLE_STATUSES.has(jobStatus)) {
            get().updateJobStatus(
              jobId,
              ReportJobStatus.PENDING,
              job.reportId,
            );
            get().updateJobProgress(jobId, progress, message);
            return false;
          } else if (jobStatus === "FAILED") {
            // Job failed
            let errMessage = message || "Video processing failed.";

            throw new Error(errMessage);
          } else {
            get().updateJobProgress(jobId, progress, message);
            return false; // Continue polling
          }
        } catch (error) {
          // Handle error cases
          let errMessage = "An error occurred while processing the video. Error found: " + ((error as Error).message || "");

          // Add specific error message from API if available
          if (isAxiosError(error) && error.response) {
            errMessage += " " + (error.response.data?.error || "");
          }

          get().updateJobStatus(
            jobId,
            ReportJobStatus.FAILED,
            undefined,
            errMessage
          );
          return true; // Stop polling
        }
      },

      /**
       * Starts polling for updates on a specific job
       * Sets up an interval to check job status every second
       */
      startPollingJob: (jobId) => {
        // Don't start polling if it's already active
        if (get().pollingIntervals[jobId]) return;

        // Set up polling interval (every 3 seconds)
        const intervalId = window.setInterval(async () => {
          const shouldStop = await get().checkJobStatus(jobId);
          if (shouldStop) {
            get().stopPollingJob(jobId);
          }
        }, 3000);

        // Store the interval ID
        set((state) => ({
          pollingIntervals: {
            ...state.pollingIntervals,
            [jobId]: intervalId,
          },
        }));
      },

      /**
       * Stops polling for a specific job
       * Clears the interval and removes it from the tracking object
       */
      stopPollingJob: (jobId) => {
        const { pollingIntervals } = get();
        if (pollingIntervals[jobId]) {
          clearInterval(pollingIntervals[jobId]);
          set((state) => {
            const newPollingIntervals = { ...state.pollingIntervals };
            delete newPollingIntervals[jobId];
            return { pollingIntervals: newPollingIntervals };
          });
        }
      },

      /**
       * Retries a failed job by creating a new job with the same parameters
       * 1. Removes the old job
       * 2. Makes API request to start a new job
       * 3. Adds the new job to the store
       */
      retrySingleJob: async (job) => {
        try {
          // Create the request body based on job data
          const requestBody = { jobId: job.id };

          // Make the request
          await jobsClient.post("/jobs", requestBody);

          // set job state to pending

          set((state) => ({
            jobs: state.jobs.map((j) =>
              j.id === job.id
                ? {
                    ...j,
                    status: ReportJobStatus.PENDING,
                    progress: 0,
                    error: undefined,
                    displayStartedAt: new Date(),
                  }
                : j
            ),
          }));

          // restart job status polling
          get().startPollingJob(job.id);
        } catch (error) {
          job.error = (error as any).response?.data?.error || "Failed to retry job.";
          console.error("Error retrying job:", error);
        }
      },

      /**
       * Gets the count of currently active (pending) jobs
       * @returns Number of active jobs
       */
      activeJobsCount: () => {
        return get().jobs.filter(
          (job) =>
            job.status === ReportJobStatus.PENDING ||
            job.status === ReportJobStatus.AWAITING_INPUT
        ).length;
      },

      /**
       * Checks if the maximum number of concurrent jobs has been reached
       * @returns true if max jobs reached, false otherwise
       */
      isMaxJobsReached: () => {
        return get().activeJobsCount() >= get().MAX_CONCURRENT_JOBS;
      },
      // In the event of logout, clear persisted memory in Zustand store
      clearStore: () => {
        // Stop all active polling intervals
        Object.keys(get().pollingIntervals).forEach((jobId) => {
          get().stopPollingJob(jobId);
        });

        // Reset the store to initial state
        set({
          jobs: [],
          pollingIntervals: {},
        });
      },
    }),
    {
      name: "job-storage", // unique name for localStorage key
      storage: createJSONStorage(() => localStorage),
      // Only persist the jobs array, not the polling intervals
      partialize: (state) => ({ jobs: state.jobs }),
    }
  )
);
